import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import { BookOpen, Plus, Edit, Trash2, Save, X } from 'lucide-react';

const CATEGORIES = [
  { value: 'beginner-friendly', label: 'Beginner Friendly' },
  { value: 'tools', label: 'Tools' },
  { value: 'tutorials', label: 'Tutorials' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'general-knowledge', label: 'General Knowledge' }
];

const RESOURCE_TYPES = [
  { value: 'article', label: 'Article' },
  { value: 'video', label: 'Video' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'course', label: 'Course' },
  { value: 'tool', label: 'Tool' },
  { value: 'repository', label: 'Repository' },
  { value: 'other', label: 'Other' }
];

const ManageResources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    category: 'beginner-friendly',
    type: 'article',
    isBeginnerFriendly: false,
    isActive: true
  });

  useEffect(() => {
    let cancelled = false

    const fetchResources = async () => {
      try {
        const auth = JSON.parse(localStorage.getItem('auth'))
        const token = auth?.token
        if (!token) {
          window.location.href = '/admin/login'
          return
        }
        const response = await axios.get('/admin/resources', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return
        setResources(response.data);
        setLoading(false);
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching resources:', error);
        if (error.response?.status === 401) {
          window.location.href = '/admin/login'
        }
        setLoading(false);
      }
    };

    fetchResources()
    return () => { cancelled = true }
  }, []);

  const fetchResourcesManual = async () => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      const response = await axios.get('/admin/resources', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching resources:', error);
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      
      if (editingResource) {
        await axios.put(
          `/admin/resources/${editingResource.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          '/admin/resources',
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      fetchResourcesManual();
      resetForm();
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Failed to save resource');
    }
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setFormData({
      name: resource.name,
      description: resource.description,
      url: resource.url,
      category: resource.category,
      type: resource.type,
      isBeginnerFriendly: resource.isBeginnerFriendly,
      isActive: resource.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.delete(`/admin/resources/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchResourcesManual();
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Failed to delete resource');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      url: '',
      category: 'beginner-friendly',
      type: 'article',
      isBeginnerFriendly: false,
      isActive: true
    });
    setEditingResource(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-primary shrink-0" />
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Manage Resources</h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors w-full sm:w-auto"
        >
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {showForm ? 'Cancel' : 'Add Resource'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-card rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingResource ? 'Edit Resource' : 'Add New Resource'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">URL *</label>
                <input
                  type="url"
                  required
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {RESOURCE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Description *</label>
              <textarea
                required
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isBeginnerFriendly}
                  onChange={(e) => setFormData({ ...formData, isBeginnerFriendly: e.target.checked })}
                  className="w-4 h-4 text-primary border-input rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-card-foreground">Beginner Friendly</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-primary border-input rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-card-foreground">Active</span>
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors"
              >
                <Save className="w-5 h-5" />
                {editingResource ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Resources List */}
      <div className="bg-card rounded-lg shadow-sm overflow-hidden">
        {/* Mobile view: Cards */}
        <div className="sm:hidden space-y-4 p-4">
          {resources.map((resource) => (
            <div key={resource.id} className="border border-border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0 pr-2">
                  <div className="text-sm font-medium text-card-foreground">{resource.name}</div>
                  <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{resource.description}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleEdit(resource)}
                    className="text-primary hover:text-primary/80"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(resource.id)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-1 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                  {CATEGORIES.find(c => c.value === resource.category)?.label}
                </span>
                <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                  {resource.type}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${resource.isActive ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                  {resource.isActive ? 'Active' : 'Inactive'}
                </span>
                {resource.isBeginnerFriendly && (
                  <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                    Beginner
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop view: Table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-foreground uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-foreground uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-foreground uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-foreground uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {resources.map((resource) => (
                <tr key={resource.id} className="hover:bg-secondary/50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-card-foreground">{resource.name}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-md">{resource.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                      {CATEGORIES.find(c => c.value === resource.category)?.label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                      {resource.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${resource.isActive ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                        {resource.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {resource.isBeginnerFriendly && (
                        <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                          Beginner Friendly
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(resource)}
                        className="text-primary hover:text-primary/80"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(resource.id)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {resources.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No resources yet. Add your first resource!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageResources;
